import React, { createContext, useContext, useState, useEffect } from 'react';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;

export interface Order {
    orderId: number;
    storeId?: number;
    tableId: number;
    tableName: string;
    orderNumber: number;
    status: 'NEW' | 'PROCESSING' | 'SERVED' | 'COMPLETED' | 'CANCELED';
    orderDateTime: string;
    totalPrice: number;
    settlementCode?: string;
    orderItems: {
        orderItemId?: number;
        productId: number;
        productName: string;
        quantity: number;
        price: number;
    }[];
}

export interface StaffCall {
    storeId: number;
    tableId: number;
    tableName: string;
    createdAt: string;
}

interface WebSocketContextProps {
    orders: { [tableId: number]: Order[] };
    staffCalls: StaffCall[];
}

const WebSocketContext = createContext<WebSocketContextProps>({ orders: {}, staffCalls: [] });

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [orders, setOrders] = useState<{ [tableId: number]: Order[] }>({});
    const [staffCalls, setStaffCalls] = useState<StaffCall[]>([]);

    useEffect(() => {
        if (window.location.pathname.startsWith('/order/store/')) {
            return;
        }

        const client = new Client({
            webSocketFactory: () => new SockJS(`${baseUrl}/ws`),
            reconnectDelay: 5000,
            onConnect: () => {
                console.log('WebSocket 연결됨');
                client.subscribe('/topic/orders', (message) => {
                    if (!message.body) return;

                    console.log('WebSocket 메시지 수신:', message.body);
                    const newOrder: Order = JSON.parse(message.body);
                    const tableId = Number(newOrder.tableId);

                    if (!Number.isFinite(tableId)) {
                        console.warn('tableId 없는 주문 메시지는 무시합니다:', newOrder);
                        return;
                    }

                    setOrders(prev => {
                        const prevTableOrders = prev[tableId] || [];
                        const index = prevTableOrders.findIndex(order => order.orderId === newOrder.orderId);
                        const updatedOrders = [...prevTableOrders];

                        if (index >= 0) {
                            updatedOrders[index] = newOrder;
                        } else {
                            updatedOrders.unshift(newOrder);
                        }

                        return { ...prev, [tableId]: updatedOrders };
                    });
                });

                client.subscribe('/topic/staff-calls', (message) => {
                    if (!message.body) return;

                    const staffCall: StaffCall = JSON.parse(message.body);
                    setStaffCalls(prev => [staffCall, ...prev].slice(0, 30));
                });
            },
            onStompError: (frame) => {
                console.error('WebSocket STOMP 오류:', frame.headers.message, frame.body);
            },
            onWebSocketError: (event) => {
                console.error('WebSocket 연결 오류:', event);
            },
        });

        client.activate();

        return () => {
            client.deactivate();
        };
    }, []);

    return (
        <WebSocketContext.Provider value={{ orders, staffCalls }}>
            {children}
        </WebSocketContext.Provider>
    );
};
